import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BG_META } from "@/components/status";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CreditCard, ShieldCheck, CheckCircle2, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

export default function Onboarding() {
  const { user, refreshUser } = useAuth();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState("");
  const [bg, setBg] = useState({ full_name: user?.name || "", dob: "", ssn_last4: "", consent: false });
  const [bgSubmission, setBgSubmission] = useState(null);

  const loadBg = useCallback(() => api.get("/background-check/me").then((r) => setBgSubmission(r.data.submission)), []);
  const stripeComplete = params.get("complete");
  useEffect(() => {
    loadBg();
    if (stripeComplete) {
      api.post("/stripe/onboard/complete")
        .then(() => { refreshUser(); toast.success("Stripe onboarding complete"); })
        .catch((err) => { refreshUser(); toast.error(formatApiErrorDetail(err.response?.data?.detail)); });
    }
  }, [loadBg, stripeComplete, refreshUser]);

  const bgStatusMessage = (status) => {
    if (status === "approved") {
      return <p className="text-sm text-emerald-700 mt-2 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Your background check is approved.</p>;
    }
    if (status === "pending") {
      return <p className="text-sm text-amber-700 mt-2">Submitted on {bgSubmission ? new Date(bgSubmission.created_at).toLocaleDateString() : ""}. Awaiting admin review.</p>;
    }
    if (status === "denied") {
      return <p className="text-sm text-red-700 mt-2">Your check was denied. Contact support or resubmit.</p>;
    }
    return null;
  };

  const startStripe = async () => {
    setBusy("stripe");
    try {
      const { data } = await api.post("/stripe/onboard");
      if (data.url) { window.location.href = data.url; return; }
      toast.success("Stripe payouts connected");
      await refreshUser();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const submitBg = async (e) => {
    e.preventDefault();
    if (!bg.consent) { toast.error("Consent is required"); return; }
    setBusy("bg");
    try {
      await api.post("/background-check", bg);
      toast.success("Background check submitted for review");
      await refreshUser();
      loadBg();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const bgStatus = user?.background_check_status || "not_submitted";

  return (
    <div className="space-y-6 max-w-3xl" data-testid="onboarding-page">
      <div>
        <h1 className="font-heading font-black text-3xl tracking-tight text-sapphire">Verification & Payouts</h1>
        <p className="text-muted-foreground text-sm mt-1">Complete both steps to start accepting jobs.</p>
      </div>

      {/* Stripe */}
      <div className="border border-border rounded-md bg-white p-6" data-testid="stripe-section">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-md bg-ocean/10 flex items-center justify-center"><CreditCard className="w-5 h-5 text-ocean" /></div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold text-lg text-sapphire">Stripe Connect Payouts</h2>
              {user?.stripe_onboarded && <span className="text-xs font-semibold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Connected</span>}
            </div>
            <p className="text-sm text-muted-foreground mt-1">Connect a payout account so we can transfer your earnings automatically when a job is approved.</p>
            {!user?.stripe_onboarded && (
              <Button onClick={startStripe} disabled={busy === "stripe"} className="bg-ocean hover:bg-ocean/90 mt-4" data-testid="stripe-onboard-button">
                {busy === "stripe" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Connect Stripe Account"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Background check */}
      <div className="border border-border rounded-md bg-white p-6" data-testid="bgcheck-section">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-md bg-ocean/10 flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-ocean" /></div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold text-lg text-sapphire">Background Check</h2>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded ${BG_META[bgStatus]?.cls}`}>{BG_META[bgStatus]?.label}</span>
            </div>

            {bgStatusMessage(bgStatus)}

            {(bgStatus === "not_submitted" || bgStatus === "denied") && (
              <form onSubmit={submitBg} className="mt-4 space-y-3" data-testid="bgcheck-form">
                <div>
                  <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Legal Full Name</Label>
                  <Input required value={bg.full_name} onChange={(e) => setBg({ ...bg, full_name: e.target.value })} data-testid="bg-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Date of Birth</Label>
                    <Input type="date" required value={bg.dob} onChange={(e) => setBg({ ...bg, dob: e.target.value })} data-testid="bg-dob" />
                  </div>
                  <div>
                    <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">SSN (last 4)</Label>
                    <Input maxLength={4} placeholder="1234" value={bg.ssn_last4} onChange={(e) => setBg({ ...bg, ssn_last4: e.target.value })} data-testid="bg-ssn" />
                  </div>
                </div>
                <label className="flex items-start gap-2 text-sm text-slate-600">
                  <Checkbox checked={bg.consent} onCheckedChange={(v) => setBg({ ...bg, consent: !!v })} data-testid="bg-consent" />
                  <span>I authorize Clean Slate Connect to run a background check on my behalf.</span>
                </label>
                <p className="text-xs text-muted-foreground flex items-center gap-1"><Lock className="w-3 h-3" /> Your data is encrypted and only used for verification.</p>
                <Button type="submit" disabled={busy === "bg"} className="bg-ocean hover:bg-ocean/90" data-testid="bg-submit">
                  {busy === "bg" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Background Check"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
